// Local storage utilities for saving progress without authentication

import { Difficulty } from '../types';

const STORAGE_KEY = 'sudoku_local_progress';

interface LocalProgress {
  completedPuzzles: Set<number>; // Set of completed puzzle IDs
  completedByDifficulty: Record<Difficulty, number[]>;
}

interface StoredProgress {
  completedPuzzles: number[];
  completedByDifficulty: Record<Difficulty, number[]>;
}

const defaultCompletedByDifficulty: Record<Difficulty, number[]> = {
  beginner: [],
  easy: [],
  medium: [],
  hard: [],
};

// Get local progress from localStorage
export function getLocalProgress(): LocalProgress {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return {
        completedPuzzles: new Set(),
        completedByDifficulty: { ...defaultCompletedByDifficulty },
      };
    }
    const parsed: StoredProgress = JSON.parse(stored);
    return {
      completedPuzzles: new Set(parsed.completedPuzzles || []),
      completedByDifficulty: {
        ...defaultCompletedByDifficulty,
        ...parsed.completedByDifficulty,
      },
    };
  } catch {
    return {
      completedPuzzles: new Set(),
      completedByDifficulty: { ...defaultCompletedByDifficulty },
    };
  }
}

// Save local progress to localStorage
function saveLocalProgress(progress: LocalProgress): void {
  try {
    const toStore: StoredProgress = {
      completedPuzzles: Array.from(progress.completedPuzzles),
      completedByDifficulty: progress.completedByDifficulty,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch (error) {
    console.error('Failed to save local progress:', error);
  }
}

// Mark a puzzle as completed locally
export function markPuzzleCompletedLocally(
  puzzleId: number,
  difficulty: Difficulty,
  levelNumber: number
): void {
  const progress = getLocalProgress();
  
  // Add to completed puzzles
  progress.completedPuzzles.add(puzzleId);
  
  // Add to difficulty-specific completed list (if not already there)
  if (!progress.completedByDifficulty[difficulty].includes(levelNumber)) {
    progress.completedByDifficulty[difficulty].push(levelNumber);
    progress.completedByDifficulty[difficulty].sort((a, b) => a - b);
  }
  
  saveLocalProgress(progress);
}

// Check if a puzzle is completed locally
export function isPuzzleCompletedLocally(puzzleId: number): boolean {
  const progress = getLocalProgress();
  return progress.completedPuzzles.has(puzzleId);
}

// Check if a level is unlocked based on local progress
export function isLevelUnlockedLocally(
  difficulty: Difficulty,
  levelNumber: number
): boolean {
  if (levelNumber === 1) return true;
  
  const progress = getLocalProgress();
  const completedLevels = progress.completedByDifficulty[difficulty];
  
  // Level is unlocked if the previous level is completed
  return completedLevels.includes(levelNumber - 1);
}

// Clear all local progress (useful for testing or when user logs in)
export function clearLocalProgress(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear local progress:', error);
  }
}

// Get all completed puzzle IDs (for syncing to server after login)
export function getCompletedPuzzleIds(): number[] {
  const progress = getLocalProgress();
  return Array.from(progress.completedPuzzles);
}

// Check if there is any local progress to sync
export function hasLocalProgressToSync(): boolean {
  const progress = getLocalProgress();
  return progress.completedPuzzles.size > 0;
}
